import { Attachment, IWall, User, Wall } from '@/common/classes'
import { AttachmentType, authMethods, PermissionLevel, ScreensizeType, urlRegex } from '@/constants'
import { computed, ComputedRef, reactive } from 'vue'

import useApiService from '@/composables/api'
import { APIResponse, IWallPayload, ScrapeResult } from '@/common/interfaces'

import useWallStore from './wallStore'

const apiService = useApiService()
const wallStore = useWallStore()

const _state = reactive({
  languageCode: 'en', // default to english
  screensizeType: ScreensizeType.desktop,
  noteBoardValues: [0, 0, 0, 0],
  sidebarNoteDragEvent: {} as PointerEvent,
})

interface State {
  languageCode: string
  screensizeType: ScreensizeType
  noteBoardValues: number[]
}

interface Actions {
  getCleanTimestamp(timestamp: Date): string
  shortenedUserName(user: User): string
  constructAttachmentFromScrape(url: string): Promise<Attachment | null>
  getActualUrlsFromText(text: string): string[]
  cleanUrlString(url: string): string
  sanitizeUrl(urls: string[]): string[]
  hasMinimumPermission(user: User, requiredPermission: PermissionLevel, wall?: Wall): boolean
  setSidebarNoteDragEvent(event: PointerEvent): void
  setLanguageCode(): void
  setLanguageCodeByValue(languageCode: string): void
  setScreensizeType(type: ScreensizeType): void
  copyTextToClipboard(text: string): void
  setNoteBoardValues(values: number[]): void
}

interface Getters {
  languageCode: ComputedRef<string>
  screensizeType: ComputedRef<ScreensizeType>
  isMobileView: ComputedRef<boolean>
  noteBoardValues: ComputedRef<number[]>
  sidebarNoteDragEvent: ComputedRef<PointerEvent>
}

interface ServiceInterface {
  state: State
  actions: Actions
  getters: Getters
}

function useCommonStore(): ServiceInterface {
  const actions = {
    getCleanTimestamp(timestamp: Date): string {
      const expression = _state.languageCode.includes('no')
      const translatedSlug = expression ? 'siden' : 'ago'

      timestamp = new Date(timestamp)
      let suffix = ''
      let cleanTimestamp = (new Date().getTime() - timestamp.getTime()) / 86400000
      if (cleanTimestamp < 1) {
        // within an hour or within a day

        cleanTimestamp *= 1440
        suffix = 'm'

        if (cleanTimestamp / 60 >= 1) {
          cleanTimestamp /= 60
          suffix = expression ? 't' : 'h'
        }
      } else {
        if (cleanTimestamp > 365) {
          cleanTimestamp /= 365
          suffix = expression ? 'å' : 'y'
        } else {
          suffix = 'd'
        }
      }
      return `${Math.floor(cleanTimestamp)}${suffix} ${translatedSlug}`
    },

    shortenedUserName(user: User) {
      return user.profile.fullName
        ? user.profile.fullName
            .split(' ')
            .splice(0, 2)
            .map((w: string) => w[0])
            .join('')
        : user.profile.username.substring(0, 2)
    },

    async constructAttachmentFromScrape(url: string): Promise<Attachment | null> {
      const attachment = {
        type: AttachmentType.external,
        title: '',
        description: '',
        url: '',
      } as Attachment

      const res = (await apiService.request.scrape(url)) as APIResponse<ScrapeResult>
      if ((!res.error && !res.errors) || res.data) {
        const scrapeResult = res.data as ScrapeResult
        attachment.url = scrapeResult.image
        attachment.title = scrapeResult.title
        attachment.description = scrapeResult.description
        return attachment
      } else return null
    },

    getActualUrlsFromText(text: string): string[] {
      let urls = []
      for (const word of text.split(' ')) {
        const result = word.match(urlRegex)
        if (result) urls.push(result[0])
      }
      urls = urls?.filter((u: string) => u !== undefined && u.length > 4 && ['www', 'http'].some((p) => u.includes(p)))
      return urls
    },

    cleanUrlString(url: string): string {
      //return url.replace(/https?:\/\//, '').replace(/(www\.)/, '')
      const regex = /(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]/g
      return url.match(regex)?.[0] ?? ''
    },

    sanitizeUrl(urls: string[]): string[] {
      const sanitizedUrls = []
      for (const url of urls) {
        if (url) {
          const index = url.indexOf('//')
          if (index !== -1) {
            const host = url.replace('www.', '').substring(index + 2, url.length)
            const slugIndex = host.indexOf('/')
            if (slugIndex !== -1) sanitizedUrls.push(host.substring(0, slugIndex))
            else sanitizedUrls.push(url)
          }
        }
      }
      return sanitizedUrls
    },

    hasMinimumPermission(user: User, requiredPermission: PermissionLevel, wall?: Wall): boolean {
      let currentPermissionIndex = -1
      const permissionOrder =
        requiredPermission === PermissionLevel.Copy ? [PermissionLevel.Copy] : [PermissionLevel.Join, PermissionLevel.Organize, PermissionLevel.Owner]

      if (wall) {
        if (wall.users.owner.user_id === user._id)
          return true // owner has all permissions
        else {
          const shares = wall.users.shares.filter((s) =>
            wall.users.shares.some((share) => user.profile.groups.find((g) => g.originalId === share.group_id)),
          )
          if (shares.length > 0) {
            for (const share of shares) {
              const newIndex = permissionOrder.indexOf(share.permissionLevel)
              if (newIndex > currentPermissionIndex) currentPermissionIndex = newIndex
            }
          } else return false // the wall wasn't shared with the requested user's groups
        }
      }
      return currentPermissionIndex !== -1 ? currentPermissionIndex >= permissionOrder.indexOf(requiredPermission) : false
    },

    setSidebarNoteDragEvent(event: PointerEvent) {
      _state.sidebarNoteDragEvent = event
    },

    setNoteBoardValues(values: number[]): void {
      _state.noteBoardValues = values
    },

    setLanguageCode(): void {
      const languageCode = _state.languageCode.includes('no') ? 'en' : 'no'
      _state.languageCode = languageCode
    },

    setLanguageCodeByValue(languageCode: string): void {
      _state.languageCode = languageCode
    },

    setScreensizeType(type: ScreensizeType): void {
      _state.screensizeType = type
    },
    copyTextToClipboard(text: string): void {
      navigator.clipboard.writeText(text)
    },
  }

  const getters = {
    get languageCode(): ComputedRef<string> {
      return computed(() => _state.languageCode)
    },

    get screensizeType(): ComputedRef<ScreensizeType> {
      return computed(() => _state.screensizeType)
    },

    get isMobileView(): ComputedRef<boolean> {
      return computed(() => _state.screensizeType === ScreensizeType.phone || _state.screensizeType === ScreensizeType.tablet)
    },

    get noteBoardValues(): ComputedRef<number[]> {
      return computed(() => _state.noteBoardValues)
    },

    get sidebarNoteDragEvent(): ComputedRef<PointerEvent> {
      return computed(() => _state.sidebarNoteDragEvent)
    },
  }

  const state = _state

  return { state, actions, getters }
}

export default useCommonStore
